import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

const API_URL = '/api/venues';

export const fetchVenues = createAsyncThunk(
  'venues/fetchVenues',
  async (arg, { rejectWithValue }) => {
    try {
      const includeArchived = !!(arg && arg.includeArchived);
      const { data } = await axios.get(`${API_URL}/`, {
        params: includeArchived ? { include_archived: true } : undefined,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const restoreVenue = createAsyncThunk(
  'venues/restoreVenue',
  async (venueId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}/${venueId}/restore`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const purgeVenue = createAsyncThunk(
  'venues/purgeVenue',
  async (venueId, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/${venueId}/purge`);
      return venueId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const fetchChains = createAsyncThunk(
  'venues/fetchChains',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL}/chains`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const createVenue = createAsyncThunk(
  'venues/createVenue',
  async (venueData, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}/`, venueData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const updateVenue = createAsyncThunk(
  'venues/updateVenue',
  async ({ venueId, venueData }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API_URL}/${venueId}`, venueData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const createScreen = createAsyncThunk(
  'venues/createScreen',
  async ({ venueId, screenData }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}/${venueId}/screens`, screenData);
      return { venueId, screen: data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const updateScreen = createAsyncThunk(
  'venues/updateScreen',
  async ({ screenId, screenData }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API_URL}/screens/${screenId}`, screenData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const deleteVenue = createAsyncThunk(
  'venues/deleteVenue',
  async (venueId, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/${venueId}`);
      return venueId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const uploadScreenCertificate = createAsyncThunk(
  'venues/uploadCert',
  async ({ screenId, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API_URL}/screens/${screenId}/certificate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const venuesSlice = createSlice({
  name: 'venues',
  initialState: {
    venues: [],
    chains: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearVenuesError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVenues.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchVenues.fulfilled, (state, action) => { state.loading = false; state.venues = action.payload; })
      .addCase(fetchVenues.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(fetchChains.fulfilled, (state, action) => { state.chains = action.payload; })
      .addCase(createVenue.fulfilled, (state, action) => { state.venues.push(action.payload); })
      .addCase(updateVenue.fulfilled, (state, action) => {
        const i = state.venues.findIndex(v => v.id === action.payload.id);
        if (i !== -1) state.venues[i] = action.payload;
      })
      .addCase(createScreen.fulfilled, (state, action) => {
        const venue = state.venues.find(v => v.id === action.payload.venueId);
        if (venue) venue.screens.push(action.payload.screen);
      })
      .addCase(updateScreen.fulfilled, (state, action) => {
        for (const venue of state.venues) {
          const i = venue.screens.findIndex(s => s.id === action.payload.id);
          if (i !== -1) { venue.screens[i] = action.payload; break; }
        }
      })
      .addCase(deleteVenue.fulfilled, (state, action) => {
        state.venues = state.venues.filter(v => v.id !== action.payload);
      })
      .addCase(uploadScreenCertificate.fulfilled, (state, action) => {
        for (const venue of state.venues) {
          const i = venue.screens.findIndex(s => s.id === action.payload.id);
          if (i !== -1) { venue.screens[i] = action.payload; break; }
        }
      });
  },
});

export const { clearVenuesError } = venuesSlice.actions;
export default venuesSlice.reducer;
